import { Link } from "react-router-dom";
import {
  AlertTriangle, BarChart3, Box, CheckCircle2,
  FilePlus2, Package, PackageCheck, Settings, Trash2, Wrench,
} from "lucide-react";
import { assetApi } from "../api";
import { useAsyncData } from "../hooks";
import {
  AssetLoading, AssetError, AssetStatusBadge, fmt, PageHeader, BackBtn, LinkBtn,
} from "../components/AssetUI";
import { useAuth } from "#/context/AuthProvider";

function StatCard({ label, value, sub, icon: Icon, tone, to }: {
  label: string; value: string | number; sub?: string;
  icon: React.ComponentType<{ className?: string }>; tone: "indigo" | "emerald" | "blue" | "amber" | "red" | "slate";
  to?: string;
}) {
  const colors = {
    indigo:  "bg-indigo-50  border-indigo-100  text-indigo-700",
    emerald: "bg-emerald-50 border-emerald-100 text-emerald-700",
    blue:    "bg-blue-50    border-blue-100    text-blue-700",
    amber:   "bg-amber-50   border-amber-100   text-amber-700",
    red:     "bg-red-50     border-red-100     text-red-700",
    slate:   "bg-slate-50   border-slate-100   text-slate-600",
  }[tone];
  const inner = (
    <div className={`rounded-2xl border p-5 shadow-sm transition-shadow hover:shadow-md ${colors}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide opacity-70">{label}</span>
        <Icon className="h-5 w-5 opacity-50" />
      </div>
      <div className="mt-3 text-3xl font-bold">{value}</div>
      {sub && <div className="mt-1 text-xs opacity-60">{sub}</div>}
    </div>
  );
  return to ? <Link to={to}>{inner}</Link> : <div>{inner}</div>;
}

function ActionCard({ to, icon: Icon, title, description }: { to: string; icon: React.ComponentType<{ className?: string }>; title: string; description: string }) {
  return (
    <Link to={to} className="flex items-start gap-3 rounded-2xl border bg-white p-4 shadow-sm transition-all hover:border-indigo-200 hover:shadow-md">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-indigo-50">
        <Icon className="h-5 w-5 text-indigo-600" />
      </div>
      <div>
        <div className="font-semibold text-slate-800">{title}</div>
        <div className="mt-0.5 text-xs text-slate-400">{description}</div>
      </div>
    </Link>
  );
}

export default function AssetDashboardPage() {
  const { hasRole, hasAnyPermission, hasPermission } = useAuth();
  const isAdmin = hasRole("SUPER_ADMIN") || hasRole("ADMIN");
  const canCreate   = isAdmin || hasAnyPermission(["ASSET_CREATE", "ASSET_MANAGE"]);
  const canAssign   = isAdmin || hasAnyPermission(["ASSET_ASSIGN", "ASSET_MANAGE"]);
  const canDispose  = isAdmin || hasAnyPermission(["ASSET_DISPOSE", "ASSET_MANAGE"]);
  const canImport   = isAdmin || hasAnyPermission(["ASSET_IMPORT", "ASSET_MANAGE"]);
  const canReport   = isAdmin || hasAnyPermission(["ASSET_REPORT_VIEW", "ASSET_MANAGE"]);
  const canSetup    = isAdmin || hasPermission("ASSET_MANAGE");

  const stats    = useAsyncData(() => assetApi.dashboardStats(), []);
  const recent   = useAsyncData(() => assetApi.listAssets(0, 5), []);
  const statsByS = useAsyncData(() => assetApi.statsByStatus(), []);
  const disposals = useAsyncData(() => assetApi.listDisposals(0, 3, { status: "PENDING" }), []);

  const s = stats.data;

  return (
    <div className="space-y-6">
      <PageHeader
        breadcrumb="Asset Management"
        title="Dashboard"
        actions={
          <div className="flex gap-2">
            <BackBtn to="/" label="Home" />
            {canCreate && <LinkBtn to="/assets/new" variant="primary" icon={FilePlus2}>Add Asset</LinkBtn>}
          </div>
        }
      />

      {/* ── Stat grid ── */}
      {stats.loading ? <AssetLoading label="Loading stats…" /> : stats.error ? <AssetError message={stats.error} onRetry={stats.reload} /> : s ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard label="Total Assets"      value={s.totalAssets}            icon={Package}     tone="indigo"  to="/assets/list" />
          <StatCard label="Available"         value={s.availableAssets}        icon={CheckCircle2} tone="emerald" to="/assets/list" />
          <StatCard label="Assigned"          value={s.assignedAssets}         icon={PackageCheck} tone="blue"   to="/assets/assignments" />
          <StatCard label="Under Maintenance" value={s.underMaintenanceAssets} icon={Wrench}       tone="amber"  />
        </div>
      ) : null}

      {s && (
        <div className="grid gap-4 sm:grid-cols-3">
          <StatCard label="Flagged for Disposal" value={s.flaggedForDisposal}   icon={Trash2}   tone="red"   to={canDispose ? "/assets/disposals" : undefined} sub={`${s.pendingDisposalAssets} pending approval`} />
          <StatCard label="Total Purchase Cost"  value={fmt(s.totalPurchaseCost)} icon={BarChart3} tone="indigo" />
          <StatCard label="Current Book Value"   value={fmt(s.totalCurrentValue)} icon={BarChart3} tone="slate" />
        </div>
      )}

      {/* ── Pending actions banner ── */}
      {s && s.pendingDisposalAssets > 0 && canDispose && (
        <div className="flex flex-wrap gap-3">
          <Link to="/assets/disposals" className="flex items-center gap-3 rounded-2xl border border-red-200 bg-red-50 px-5 py-3 shadow-sm hover:shadow-md">
            <Trash2 className="h-5 w-5 text-red-500" />
            <div>
              <span className="font-bold text-red-900">{s.pendingDisposalAssets}</span>
              <span className="ml-1 text-sm text-red-700">disposal request{s.pendingDisposalAssets !== 1 ? "s" : ""} pending review</span>
            </div>
          </Link>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        {/* ── Left: recent assets + by-status ── */}
        <div className="space-y-6 lg:col-span-2">

          {/* Recent assets */}
          <div className="overflow-hidden rounded-2xl border bg-white shadow-sm">
            <div className="flex items-center justify-between border-b px-5 py-4">
              <h2 className="font-semibold text-slate-900">Recently Added Assets</h2>
              <Link to="/assets/list" className="text-sm font-semibold text-indigo-600 hover:underline">View all →</Link>
            </div>
            {recent.loading ? (
              <div className="p-5"><AssetLoading /></div>
            ) : (
              <div className="divide-y">
                {(recent.data?.content ?? []).map((a) => (
                  <Link key={a.id} to={`/assets/${a.id}`} className="flex items-center gap-4 px-5 py-3.5 transition hover:bg-slate-50">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-xs font-bold text-indigo-700">
                      {a.categoryName.slice(0, 2).toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-semibold text-slate-800">{a.name}</div>
                      <div className="text-xs text-slate-400">{a.assetNumber} · {a.categoryName}</div>
                    </div>
                    <AssetStatusBadge value={a.status} />
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* By status */}
          <div className="overflow-hidden rounded-2xl border bg-white shadow-sm">
            <div className="border-b px-5 py-4">
              <h2 className="font-semibold text-slate-900">Status Breakdown</h2>
            </div>
            <div className="divide-y">
              {(statsByS.data ?? []).filter((r) => r.count > 0).map((r) => {
                const max = Math.max(...(statsByS.data?.map((x) => x.count) ?? [1]));
                return (
                  <div key={r.status} className="px-5 py-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium text-slate-700">{r.status.replace(/_/g, " ")}</span>
                      <span className="font-bold text-slate-900">{r.count} <span className="font-normal text-slate-400">· {fmt(r.totalCurrentValue)}</span></span>
                    </div>
                    <div className="mt-1.5 h-1.5 rounded-full bg-slate-100">
                      <div className="h-full rounded-full bg-indigo-500" style={{ width: `${(r.count / max) * 100}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* ── Right column ── */}
        <div className="space-y-6">

          {/* Quick actions */}
          <div className="overflow-hidden rounded-2xl border bg-white shadow-sm">
            <div className="border-b px-5 py-4"><h2 className="font-semibold text-slate-900">Quick Actions</h2></div>
            <div className="grid gap-2 p-4">
              {canCreate  && <ActionCard to="/assets/new"           icon={FilePlus2}    title="Add New Asset"     description="Register a new company asset" />}
              {canAssign  && <ActionCard to="/assets/assignments"    icon={PackageCheck} title="Assignments"       description="Assign/return assets" />}
              {canDispose && <ActionCard to="/assets/disposals"      icon={Trash2}       title="Disposal Requests" description="Manage asset disposal workflow" />}
              {canImport  && <ActionCard to="/assets/import"         icon={Box}          title="Import Assets"     description="Bulk import via Excel file" />}
              {canReport  && <ActionCard to="/assets/reports"        icon={BarChart3}    title="Reports"           description="Generate asset reports" />}
              {canSetup   && <ActionCard to="/assets/setup"          icon={Settings}     title="Setup"             description="Categories, locations & config" />}
              {!canCreate && !canAssign && !canDispose && !canImport && !canReport && !canSetup && (
                <p className="px-1 py-2 text-xs text-slate-400">No quick actions available for your account.</p>
              )}
            </div>
          </div>

          {/* Pending disposals */}
          {canDispose && (disposals.data?.content.length ?? 0) > 0 && (
            <div className="overflow-hidden rounded-2xl border border-red-100 bg-white shadow-sm">
              <div className="flex items-center justify-between border-b border-red-100 px-5 py-4">
                <h2 className="font-semibold text-slate-900">Pending Disposals</h2>
                <Link to="/assets/disposals" className="text-xs font-semibold text-red-600 hover:underline">View all →</Link>
              </div>
              <div className="divide-y divide-red-50">
                {disposals.data!.content.map((d) => (
                  <Link key={d.id} to="/assets/disposals" className="flex items-start gap-3 px-5 py-3 transition hover:bg-red-50">
                    <Trash2 className="mt-0.5 h-4 w-4 shrink-0 text-red-400" />
                    <div className="min-w-0">
                      <div className="truncate text-sm font-semibold text-slate-800">{d.assetName}</div>
                      <div className="text-xs text-slate-400">{d.assetNumber} · {d.reason}</div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* My assets alert */}
          {s && s.flaggedForDisposal > 0 && canDispose && (
            <div className="flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4">
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
              <div className="text-sm text-amber-800">
                <span className="font-bold">{s.flaggedForDisposal}</span> asset{s.flaggedForDisposal !== 1 ? "s" : ""} flagged for disposal. <Link to="/assets/disposals" className="font-semibold underline">Review →</Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
